/**
 * Real-time list of the signed-in user's conversation folders.
 *
 * Subscribes to `users/{uid}/folders` and ordena por nome (pt-BR, localeCompare)
 * no cliente — a coleção é pequena e o nome pode ser renomeado a qualquer momento.
 */
import type { FolderDoc } from '@agente/shared'
import { onSnapshot } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { foldersCol } from '@/lib/firestore'

export interface UseFoldersResult {
  folders: FolderDoc[]
  loading: boolean
  error: string | null
}

export function useFolders(): UseFoldersResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [folders, setFolders] = useState<FolderDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) {
      setFolders([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    const unsubscribe = onSnapshot(
      foldersCol(uid),
      (snap) => {
        const next = snap.docs
          .map((d) => ({ ...d.data(), id: d.id }))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        setFolders(next)
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid])

  return { folders, loading, error }
}
